import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, catchError, map, of, tap } from 'rxjs';
import { API_V1_URL } from '../config/api.config';
import { ThemeService } from './theme.service';
import { AuthService } from './auth.service';
interface EinstellungDto {
    schluessel?: string;
    wert?: string;
    Schluessel?: string;
    Wert?: string;
}

@Injectable({
    providedIn: 'root',
})
export class AppSettingsService {
    private readonly http = inject(HttpClient);
    private readonly themeService = inject(ThemeService);
    private readonly authService = inject(AuthService);

    private readonly darkModeKey = 'ui.darkMode';
    private readonly sidebarCollapsedKey = 'ui.sidebarCollapsed';

    readonly isSidebarCollapsed = signal(false);

    loadUserSettings(): Observable<void> {
        this.applyCachedSettings();

        return this.http
            .get<EinstellungDto[]>(`${API_V1_URL}/settings`)
            .pipe(
                tap((settings) => {
                    const map = this.toSettingsMap(settings);

                    const darkMode = this.toBoolean(map[this.darkModeKey] ?? 'false');
                    const sidebarCollapsed = this.toBoolean(map[this.sidebarCollapsedKey] ?? 'false');

                    this.themeService.setTheme(darkMode ? 'dark' : 'light');
                    this.isSidebarCollapsed.set(sidebarCollapsed);

                    this.saveSettingToCache(this.darkModeKey, String(darkMode));
                    this.saveSettingToCache(this.sidebarCollapsedKey, String(sidebarCollapsed));
                }),
                map(() => void 0),
                catchError(() => of(void 0))
            );
    }

    resetSettingsToDefault(): void {
        this.themeService.forceLightTheme();
        this.isSidebarCollapsed.set(false);
    }

    toggleDarkMode(): void {
        const nextTheme = this.themeService.currentTheme() === 'dark' ? 'light' : 'dark';
        const isDarkMode = nextTheme === 'dark';

        this.themeService.setTheme(nextTheme);
        this.saveSettingToCache(this.darkModeKey, String(isDarkMode));

        this.saveSettings({
            [this.darkModeKey]: String(isDarkMode),
        }).subscribe();
    }

    toggleSidebarCollapsed(): void {
        this.setSidebarCollapsed(!this.isSidebarCollapsed());
    }

    setSidebarCollapsed(collapsed: boolean): void {
        this.setSidebarCollapsedLocal(collapsed);

        this.saveSettings({
            [this.sidebarCollapsedKey]: String(collapsed),
        }).subscribe();
    }

    private setSidebarCollapsedLocal(collapsed: boolean): void {
        this.isSidebarCollapsed.set(collapsed);
        this.saveSettingToCache(this.sidebarCollapsedKey, String(collapsed));
    }

    private applyCachedSettings(): void {
        const cachedDarkMode = this.getSettingFromCache(this.darkModeKey);
        const cachedSidebarCollapsed = this.getSettingFromCache(this.sidebarCollapsedKey);

        if (cachedDarkMode !== null) {
            this.themeService.setTheme(this.toBoolean(cachedDarkMode) ? 'dark' : 'light');
        }

        if (cachedSidebarCollapsed !== null) {
            this.isSidebarCollapsed.set(this.toBoolean(cachedSidebarCollapsed));
        }
    }

    private getSettingFromCache(key: string): string | null {
        return localStorage.getItem(this.getUserSettingKey(key));
    }

    private saveSettingToCache(key: string, value: string): void {
        localStorage.setItem(this.getUserSettingKey(key), value);
    }

    private getUserSettingKey(key: string): string {
        const userId = this.authService.getCurrentUserId() ?? 'anonymous';

        return `ofs2:${userId}:${key}`;
    }

    private saveSettings(settings: Record<string, string>): Observable<void> {
        return this.http
            .put<void>(`${API_V1_URL}/settings`, settings)
            .pipe(catchError(() => of(void 0)));
    }

    private toSettingsMap(settings: EinstellungDto[]): Record<string, string> {
        return settings.reduce<Record<string, string>>((result, item) => {
            const key = item.schluessel ?? item.Schluessel;
            const value = item.wert ?? item.Wert;

            if (key && value !== undefined) {
                result[key] = value;
            }

            return result;
        }, {});
    }

    private toBoolean(value: string): boolean {
        return value.toLowerCase() === 'true';
    }
}